import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { json, redirect, type LoaderFunctionArgs } from '@remix-run/node';
import {
    useLoaderData,
    useLocation,
    useNavigate,
    useSearchParams,
    type ShouldRevalidateFunction,
} from '@remix-run/react';
import invariant from 'tiny-invariant';
import { prefetchCategoryImages } from '~/modules/category/util/prefetch-category-images';
import {
    getCanonicalHreflang,
    createMetaTitle,
    createMetaDescription,
    fullCountryNameFromCode,
    createOGTags,
    mergeMeta,
} from '~/modules/seo';
import { createFAQSchema } from '~/modules/seo/schema/faq-page-schema';
import { createProductsItemListSchema } from '~/modules/seo/schema/item-list-schema';
import { createLDSchema } from '~/modules/seo/util/create-ld-schema';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import { getDirectoryPageMeta } from '@modules/category/api';
import { pxToRem } from '@archipro-design/aria';
import {
    useTheme,
    Container,
    Box,
    useAppDisplayModeContext,
} from '@archipro-design/aria';
import { PermissionEnum } from 'generated/graphql';
import { NoResultsSection } from '@modules/category/component/no-results/NoResultsSection';
import { getElasticSearchResults } from '@modules/search/api/elasticsearch.server';
import type {
    ElasticSearchAggregateProps,
    ElasticSearchAggregations,
    ElasticSearchQueryProps,
    ElasticSearchSortOptions,
    SearchResults,
} from '@modules/search/type/api';
import type { SearchResultItem } from '@modules/search/type/hits';
import { CategoryFilterSection } from '@modules/category/component/category-filter-section/CategoryFilterSection';
import { CategoryFilterNav } from '@modules/category/component/category-filter-section/CategoryFilterNav';
import { createESAggregationProps } from '@modules/search/util/create-es-aggregation-props';
import { createFilterStates } from '@modules/search/util/create-filter-states.server';
import { adRequest } from '@modules/adverts/api';
import AdvertBannerTile from '@modules/adverts/component/advert-banner-tile/AdvertBannerTile';
import { createPaginationState } from '@modules/pagination/util/create-pagination-state.server';
import { TitleSectionMobile } from '~/modules/category/component/title-section/TitleSection.mobile';
import { TitleSectionDesktop } from '~/modules/category/component/title-section/TitleSection.desktop';
import { getPageLocalisation } from '~/modules/seo/api/get-page-localisation.server';
import { CollapsiblePageLayout } from '~/modules/category/component/collapsible-page-layout/CollapsiblePageLayout';
import { CollapsibleToggle } from '~/modules/category/component/collapsible-page-layout/CollapsibleToggle';
import {
    USER_PREFERENCE_KEY,
    useSearchFilters,
} from '~/modules/search/hook/use-search-filters';
import type { FilterStatesKey } from '~/modules/search/type/filter-state';
import AppliedFilters from '@modules/search/components/search-filter-section/AppliedFilters';
import { resetFilterStates } from '@modules/search/util/reset-filter-states';
import type { AdvertCreative } from '@modules/adverts/type';
import { useTracker } from '@archipro-website/tracker';
import { getDirectoryFullName } from '~/modules/category/util/directory-helpers';
import { useFilterDrawerPreference } from '@modules/search/hook/use-filter-drawer-preference';
import { getSession } from '~/modules/root/util/session.server';
import TitleSectionFilterMobile from '~/modules/category/component/title-section/TitleSectionFilter.mobile';
import { hasFeature, type APHandle } from '~/modules/root';
import { shuffleArray } from '~/utils/arrayHelper';
import { useLocationMemo } from '@modules/adverts/hook/use-location-memo';
import { getProductCategories } from '~/modules/products-category/api';
import { parseURLToESQueryParams } from '~/modules/search/util/filter-url-helpers';
import { getString, parseURL } from '@archipro-website/top-navigation';
import { validateSort } from '~/modules/search/util/apply-es-search-sort';
import DetailedProductSpliter from '~/modules/products-category/component/DetailedProductSpliter';
import ArchizeenAdvertTile from '~/modules/tile/component/archizeen-tile/ArchizeenAdvertTile';
import { getArticleTileViewModelFromAd } from '~/modules/adverts/utils/map-ad-data-to-tile.server';
import type { ArticleTileViewModel } from '~/modules/article';
import { getRouteData } from '~/modules/root/util/get-route-data';
import { noIndexProductCategory } from '~/modules/seo/util/no-index-product-category';
import { isFirstPage } from '~/modules/search/util/search-param-helper';
import { getAppliedFilterNumber } from '~/modules/search/util/filter-state-util';
import { getPermissions } from '~/modules/user';
import findLastIndex from 'lodash.findlastindex';
import { getUrlHasFilters } from '~/modules/search/util/get-url-has-filters.server';
import {
    DIRECTORY_IMAGE_WIDTH_DESKTOP_QUARTER,
    DIRECTORY_IMAGE_WIDTH_MOBILE_QUARTER,
} from '@modules/root/util/get-directory-tile-image-props';
import BackToTopButton from '@modules/directory/component/BackToTopButton';
import { getProductsFromESSearchResults } from '@modules-rocco/search/data-mappers/tile-product';
import type { ProductTileViewModel as ProductTileViewModelRocco } from '@rocco/ui/tile-product/view-models/product';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import { useTilesAdminData } from '~/modules-rocco/admin/hooks/use-tiles-admin-data';
import { CategoryPageTileWithAdminActions } from '~/ui-rocco/pages/category-page/CategoryPageTileWithAdminActions';
import { Grid } from '@rocco/ui/layout/components/Grid';
import { SearchIndexes } from '~/modules-rocco/search/types/enum/search-indexes';
import { CategoryPageSearchIsland } from '~/ui-rocco/pages/category-page/CategoryPageSearchIsland';
import { mapCategoryCountToPlaceholder } from '~/modules-rocco/categories/data-mappers/category-count-to-placeholder';
import { getActiveDirectoryCategoriesCount } from '~/modules-rocco/categories/api/get-active-directory-categories-count.server';
import { CategoryPageRecentlyViewed } from '~/ui-rocco/pages/category-page/CategoryPageRecentlyViewed';
import { getRecentlyViewedServer } from '~/modules-rocco/recently-viewed/api/get-recently-viewed.server';
import { mapProductToTileViewModel } from '~/modules-rocco/products/data-mappers/tile-product';
import { useCategoryTilesTracking } from '@ui-rocco/hooks/use-category-tiles-tracking';
import { Pagination as TilePagination } from '@rocco/ui/pagination/components/Pagination';
import { CategoryPageFAQ } from '~/ui-rocco/pages/category-page/CategoryPageFAQ';
import type { ProfessionalTileViewModel } from '@rocco/ui/tile-professional/view-models/professional';
import { getProfessionalsFromESSearchResults } from '~/modules-rocco/search/data-mappers/tile-professional';
import { CheckboxBlankLine, CheckboxFill } from '@archipro-design/icons';
import { buildPaginationUrl } from '@modules-rocco/pagination/util/build-pagination-url';
import { Button as RoccoButton } from '@archipro/rocco/components/button';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';
import { useOpenLogin } from '~/utils-rocco/hooks/open-login-modal';
import { useUser } from '~/modules/user/hook/use-user';
import { VirtualizedGrid } from '~/components/VirtualizedGrid';
import { cn } from '@rocco/utils/cn';
import { useDeviceBrowserContext } from '@archipro-design/aria';
import { getItemToAmplify } from '~/modules/professional/utils/get-item-to-amplify.server';
import { AnalyticsAmplifierTile } from '@rocco/ui/tile-analytics-amplifier/components/AnlyticsAmplifier';
import { useABTestIsOn } from '~/modules/root/hook/use-growthbook';
import { useWindowSize } from '~/modules/root/hook/useWindowSize';
import { PageBottomSection } from '~/ui-rocco/pages/shared/PageBottomSection';
import { useCurrentCategory } from '@rocco/ui/website-header/hooks/use-current-category';

export const meta = mergeMeta<typeof loader>(
    () => {
        return [];
    },
    (props) => {
        const { data, matches } = props;
        const { rootData, appData } = getRouteData(matches);

        const title = createMetaTitle(
            props,
            [
                data?.localisation?.Title,
                data?.directoryPageMeta?.category
                    ? `Buy ${data?.directoryPageMeta?.category} Online`
                    : 'Page Not Found',
            ],
            true,
            data?.pagination?.page
        );

        invariant(
            rootData?.localisation.country,
            'rootData is required for product category SEO'
        );

        const description = createMetaDescription([
            data?.localisation?.Description,
            ` Browse a wide range of ${
                data?.directoryPageMeta?.category
            } on ArchiPro. We offer high-quality products from leading brands in ${fullCountryNameFromCode(
                rootData.localisation.country
            )}. Shop now!`,
        ]);

        const productsListSchema = createProductsItemListSchema(props);
        const FAQSchema = createFAQSchema(data?.localisation?.FAQSchemas ?? []);
        const ldSchema = createLDSchema([productsListSchema, FAQSchema]);

        const numberTilesToPreload = data?.appDisplayMode === 'desktop' ? 4 : 2;
        const preloadImageLinks = data?.searchResult?.hits
            ? prefetchCategoryImages(
                  data.searchResult.hits,
                  numberTilesToPreload,
                  getImagesSizes({
                      desktop: DIRECTORY_IMAGE_WIDTH_DESKTOP_QUARTER,
                      mobile: DIRECTORY_IMAGE_WIDTH_MOBILE_QUARTER,
                  }),
                  data?.appDisplayMode === 'mobile'
              )
            : [];

        const metaDescriptors = [
            { title },
            { name: 'description', content: description },
            ...createOGTags({
                meta: props,
                title,
                description,
                type: 'website',
            }),
            ...getCanonicalHreflang(props),
            ...ldSchema,
            ...preloadImageLinks,
        ];

        return noIndexProductCategory(
            metaDescriptors,
            data?.url ?? '',
            data?.productCategory?.customSeoOnFilters,
            appData?.user?.FeatureFlags
        );
    }
);

export const handle: APHandle = {
    isCategoryPage: true,
    hideContactArchiPro: false,
};

const CATEGORY_IGNORE_FILTER_KEYS: FilterStatesKey[] = [
    'libraryRanges',
    'designers',
    'brands',
    'awards',
];

const DEFAULT_PRODUCT_PAGE_SIZE = 40;
const DEFAULT_SUPPLIERS_PAGE_SIZE = 39;
const DEFAULT_SORT: ElasticSearchSortOptions = 'featured';
export const PRODUCT_TILE_EXPANDED_WIDTH = 368;
export const PRODUCT_TILE_COLLAPSED_WIDTH = 340;
export const PRODUCT_TILE_PAGING_WIDTH = 175;
export const PRODUCT_TILE_BULLET_SIZE = 11;

export const shouldRevalidate: ShouldRevalidateFunction = ({
    defaultShouldRevalidate,
    formAction,
}) => {
    if (formAction?.includes('search')) {
        return false;
    }
    return defaultShouldRevalidate;
};

export const loader = async (args: LoaderFunctionArgs) => {
    const { request } = args;
    const { appDisplayMode, config, user, featureFlags } = args.context;

    const isSuppliersEndpoint = request.url.includes('/suppliers');

    const originalUrl = new URL(request.url);
    const url = isSuppliersEndpoint
        ? new URL(request.url.replace('/suppliers', ''))
        : originalUrl;
    const searchParams = url.searchParams;

    const session = await getSession(request);
    const isDesktop = appDisplayMode === 'desktop';

    const { basePath, params } = parseURL(url.pathname + url.search);

    // Get the category from the URL path
    const urlSegment = url.pathname.split('/').pop() || undefined;

    const urlSort = validateSort(
        params ? getString(params, 'sort') : undefined
    );

    const hasFilters = getUrlHasFilters(url);

    const amplifiedItems = urlSegment
        ? await getItemToAmplify({
              context: args.context,
              link: url.pathname,
              limit: isDesktop ? 5 : 4,
          })
        : null;

    console.log('After getItemToAmplify call:', {
        amplifiedItems,
    });

    const queryES = async (pageSize: number) => {
        const { queryParams } = parseURLToESQueryParams(
            `${url.pathname.replace('/suppliers', '')}?${searchParams.toString()}`,
            'product',
            { visibilities: ['InLibrary'], pageSize },
            DEFAULT_SORT
        );

        const updateUserPreference =
            searchParams.get(USER_PREFERENCE_KEY) === '1';

        if (updateUserPreference) {
            session.setUserPreferences({
                forSale: queryParams.filters?.isPurchasable ?? false,
            });
            searchParams.delete(USER_PREFERENCE_KEY);
            throw redirect(
                `${url.pathname}${isSuppliersEndpoint ? '/suppliers' : ''}?${decodeURIComponent(searchParams.toString())}`,
                {
                    headers: await session.getHeaders(),
                }
            );
        }

        const aggregationProps = createESAggregationProps({
            ...queryParams,
            searchParams,
        });

        let searchFilters = queryParams.filters;

        if (isSuppliersEndpoint) {
            // this cal overrides product aggregation
            // we simply need all professionals aggregated on products
            const professionalAggregations = await getElasticSearchResults<
                ElasticSearchAggregations,
                ElasticSearchAggregateProps
            >({
                ...args.context,
                path: 'aggregate',
                queryParams: {
                    ...aggregationProps,
                    aggregations: {
                        aggProfessionals: {
                            search: '',
                            size: 1000,
                        },
                    },
                },
                request,
            });
            if (
                professionalAggregations &&
                professionalAggregations.professionals
            ) {
                const professionals = professionalAggregations.professionals;

                // we need to select all available professionals, so copy items -> selectedItems
                professionals.selectedItems = { ...professionals.items };

                // here we need to swap data in current queryParams to be able to fetch professionals
                queryParams.searchType = 'professional';
                // we need to keep queryParams.filters as is, so the page could show the correct filters
                searchFilters = {
                    professionals: professionals.items.map((i) => i.URLSegment),
                };
            }
        }

        const getSearchResult = () => {
            return getElasticSearchResults<
                SearchResults<SearchResultItem>,
                ElasticSearchQueryProps
            >({
                ...args.context,
                path: 'search',
                queryParams: {
                    ...queryParams,
                    filters: searchFilters,
                },
                request,
            });
        };

        const getAggregations = () => {
            return getElasticSearchResults<
                ElasticSearchAggregations,
                ElasticSearchAggregateProps
            >({
                ...args.context,
                path: 'aggregate',
                queryParams: aggregationProps,
                request,
            });
        };

        return Promise.all([
            queryParams,
            aggregationProps,
            getSearchResult(),
            getAggregations(),
        ]);
    };

    const promotedAdsPromise = adRequest({
        type: 'PromotedDirectoryPlacement',
        requestUrl: request.url,
        addPathContext: ['region'],
        ...args.context,
        request,
    });

    let [
        directoryPageMeta,
        productCategory,
        recentlyViewedProductsResult,
        ads,
        localisation,
        promotedAds,
        esResults,
        activeDirectoryCategoriesCount,
    ] = await Promise.all([
        getDirectoryPageMeta({
            ...args.context,
            url,
        }),
        getProductCategories({
            ...args.context,
            args,
            config,
        }),
        getRecentlyViewedServer({ type: 'Products' }, args.context),
        adRequest({
            type: 'CategoryPlacement',
            requestUrl: request.url,
            addPathContext: ['region'],
            ...args.context,
            request,
        }),
        getPageLocalisation(args),
        promotedAdsPromise,
        // Waterfall call to query Promoted Ads and products.
        // We need to wait for Promoted Ads to be able to determine if we need to fetch one less product.
        (async () => {
            const promotedAdsResult = await promotedAdsPromise;
            const pageSize = isSuppliersEndpoint
                ? DEFAULT_SUPPLIERS_PAGE_SIZE
                : DEFAULT_PRODUCT_PAGE_SIZE;

            return queryES(
                isDesktop &&
                    isFirstPage(searchParams) &&
                    promotedAdsResult &&
                    !hasFilters &&
                    promotedAdsResult.length > 0 // promoted article just for desktop atm. might add mobile later.
                    ? pageSize - 1
                    : pageSize
            );
        })(),
        getActiveDirectoryCategoriesCount({}, args.context),
    ]);

    const [queryParams, aggregationProps, searchResult, aggregations] =
        esResults;

    const hasMarketingView = getPermissions(
        user,
        PermissionEnum.BmMarketingView
    );

    const facetFilterFeatureEnabled = hasFeature(featureFlags, 'filter_facet');

    const searchParamsForSale = queryParams.filters?.isPurchasable ?? false;
    // block forSale preference for users without shopnav feature
    const userPreferenceForSale =
        session.getUserPreferences()?.forSale ?? false;

    if (!isSuppliersEndpoint && !searchParamsForSale && userPreferenceForSale) {
        // if we have forSale items, forSale filter not applied and user prefer to see forSale items, add the forSale filter
        searchParams.set('forSale', '1');
        throw redirect(`${url.pathname}?${searchParams.toString()}`);
    }

    const pagination = createPaginationState({
        request,
        totalCount: searchResult?.nbHits ?? 0,
        maxDisplayCount: searchResult?.maxResultWindow,
        defaultPageSize: isSuppliersEndpoint
            ? DEFAULT_SUPPLIERS_PAGE_SIZE
            : DEFAULT_PRODUCT_PAGE_SIZE,
    });

    const filterStates = aggregations
        ? createFilterStates({
              aggregations,
              appliedFilters: queryParams.filters,
              aggregationsParams: aggregationProps.aggregations,
              searchType: 'product',
              categoryLink: basePath,
              rootText: 'All',
              sortBy: urlSort,
              facetFilterFeatureEnabled,
          })
        : null;

    const searchIslandPlaceholders = mapCategoryCountToPlaceholder(
        activeDirectoryCategoriesCount?.products ?? [],
        `Search products`,
        'products'
    );

    const appliedFilterCount = filterStates
        ? getAppliedFilterNumber(filterStates, ['searchLocation'])
        : 0;

    let productTilesRocco: TileViewModel<ProductTileViewModelRocco>[] = [];
    let professionalTilesRocco: TileViewModel<ProfessionalTileViewModel>[] = [];
    let adsShuffled: AdvertCreative[] = [];
    let promotedAdTiles: ArticleTileViewModel[] = [];
    let enableAwardBadges = false;

    if (isSuppliersEndpoint) {
        const professionals = getProfessionalsFromESSearchResults(searchResult);
        professionalTilesRocco = professionals.map((professional) => ({
            aspectRatio: 'default',
            model: professional,
        }));
    } else {
        const products = getProductsFromESSearchResults(searchResult, null);
        productTilesRocco = products.map((product) => ({
            aspectRatio: 'square',
            model: product,
        }));

        adsShuffled = ads ? shuffleArray(ads) : [];

        promotedAdTiles =
            shuffleArray(promotedAds)?.reduce((acc, ad) => {
                const tile = getArticleTileViewModelFromAd(ad);
                if (tile) {
                    acc.push(tile);
                }
                return acc;
            }, [] as ArticleTileViewModel[]) ?? [];

        enableAwardBadges = hasFeature(featureFlags, 'award_badges');
    }

    return json(
        {
            isSuppliersEndpoint,
            appDisplayMode,
            directoryPageMeta,
            searchResult,
            productCategory,
            productTilesRocco,
            professionalTilesRocco,
            pagination,
            recentlyViewedProducts:
                recentlyViewedProductsResult?.RecentlyViewedProducts.map(
                    (product) =>
                        mapProductToTileViewModel(product, user, {
                            appDisplayMode,
                        })
                ) ?? [],
            categoryDescription: directoryPageMeta?.directoryHeaderDescription,
            ads: adsShuffled,
            promotedAds: promotedAdTiles,
            localisation,
            url: url.pathname + url.search,
            filterStates: isSuppliersEndpoint
                ? {
                      ...filterStates,
                      totalCount: searchResult?.nbHits ?? 0,
                  }
                : filterStates,
            appliedFilterCount,
            enableAwardBadges,
            hasMarketingView: isSuppliersEndpoint ? false : hasMarketingView,
            searchIslandPlaceholders,
            amplifiedItems,
        },
        {
            headers: await session.getHeaders(),
        }
    );
};

const ProductsCategoryPage = () => {
    const appDisplayMode = useAppDisplayModeContext();
    const { setCategory } = useCurrentCategory();
    const user = useUser();
    const { desktop } = appDisplayMode;
    const {
        isSuppliersEndpoint,
        categoryDescription,
        directoryPageMeta,
        recentlyViewedProducts,
        filterStates,
        hasMarketingView,
        productTilesRocco,
        professionalTilesRocco,
        searchIslandPlaceholders,
    } = useLoaderData<typeof loader>();

    const tracker = useTracker();

    const [searchParams] = useSearchParams();

    const navigate = useNavigate();
    const filtersRef = useRef<HTMLDivElement | null>(null);

    const selectedCategory = getDirectoryFullName(directoryPageMeta);
    const selectedCompanies = searchParams.getAll('professionals').join(' | ');

    useEffect(() => {
        tracker.log('productListImpression', {
            label: 'Dentsu',
            url: new URL(window.location.href),
            data: {
                category: selectedCategory,
                brand: selectedCompanies,
            },
        });
    }, [selectedCategory, selectedCompanies, tracker]);

    useEffect(() => {
        setCategory('Products');
    }, [setCategory]);

    const { filterDrawerOpen, toggleFilterDrawer } =
        useFilterDrawerPreference();

    const gridColumns = desktop ? (filterDrawerOpen ? 4 : 5) : 2;

    const searchFilters = useSearchFilters({
        category: 'product',
        initialStates: filterStates,
        isCategoryPage: true,
        ignoreFilterKeys: CATEGORY_IGNORE_FILTER_KEYS,
    });

    const searchFiltersSuppliers = useMemo(() => {
        return isSuppliersEndpoint
            ? {
                  ...searchFilters,
                  filterBarFilters: [],
                  filterStates: {
                      isPurchasable: undefined,
                      category: searchFilters.filterStates?.category,
                  },
              }
            : searchFilters;
    }, [isSuppliersEndpoint, searchFilters]);

    const handleSuppliersClick = useCallback(() => {
        // Reset all filters when switching between suppliers and products.
        navigate(isSuppliersEndpoint ? '/products' : '/products/suppliers');

        const url = new URL(window.location.href);
        const pathname = url.pathname;
        const searchParams = new URLSearchParams(url.search);
        let newPath;

        if (isSuppliersEndpoint) {
            // Going from suppliers to products - remove /suppliers + restore page
            newPath = pathname.replace('/suppliers', '');
        } else {
            // Going from products to suppliers - add /suppliers and remove page
            searchParams.delete('page');
            newPath = `/products/suppliers${pathname.replace('/products', '')}`;
        }

        const params = searchParams.toString();
        navigate(newPath + (params ? `?${params}` : ''));
    }, [isSuppliersEndpoint, navigate]);

    const hasProducts = (filterStates?.totalCount ?? 0) > 0;

    // Determine which grid to show based on data availability
    const shouldShowSupplierGrid =
        isSuppliersEndpoint && professionalTilesRocco.length > 0;
    const shouldShowProductGrid =
        !isSuppliersEndpoint && productTilesRocco.length > 0;

    const openLogin = useOpenLogin();

    // Check if the UI updates feature flag is enabled
    const uiUpdatesJune2025Enabled = hasFeature(
        user.FeatureFlags,
        'UI_updates_june_2025'
    );

    return (
        <Box>
            <Container>
                {desktop ? (
                    <TitleSectionDesktop
                        category={directoryPageMeta.directoryHeaderTitle}
                        rootCategory={
                            isSuppliersEndpoint ? 'Suppliers' : 'Products'
                        }
                        categoryDescription={categoryDescription}
                        itemsInCategory={filterStates?.totalCount ?? 0}
                        breadcrumbs={
                            isSuppliersEndpoint
                                ? directoryPageMeta.breadcrumbs.map(
                                      ({ href, label }) => ({
                                          href: `${href}/suppliers`,
                                          label,
                                      })
                                  )
                                : directoryPageMeta.breadcrumbs
                        }
                        isRoot={directoryPageMeta.isRoot}
                    />
                ) : (
                    <TitleSectionMobile
                        title={directoryPageMeta.directoryHeaderTitle}
                        categoryDescription={categoryDescription}
                        subtitle={''}
                    >
                        <TitleSectionFilterMobile
                            esCategory="product"
                            initialStates={filterStates}
                            quickFiltersBefore={
                                hasProducts
                                    ? [
                                          {
                                              key: `suppliers-tab`,
                                              iconBefore:
                                                  isSuppliersEndpoint ? (
                                                      <CheckboxFill />
                                                  ) : (
                                                      <CheckboxBlankLine />
                                                  ),
                                              children: 'Suppliers',
                                              onClick: handleSuppliersClick,
                                              selected: false,
                                              transparent: true,
                                              outlined: true,
                                              disabled: false,
                                              className: '',
                                          },
                                      ]
                                    : []
                            }
                        />
                    </TitleSectionMobile>
                )}

                {desktop && (
                    <CategoryFilterSection
                        category="product"
                        searchFilters={searchFiltersSuppliers}
                        initialStates={filterStates}
                        filterDrawerOpen={filterDrawerOpen}
                        componentBarAddon={
                            <>
                                {hasProducts ? (
                                    <RoccoButton
                                        clickAnimation="scale"
                                        color={
                                            isSuppliersEndpoint
                                                ? 'primary'
                                                : 'secondary'
                                        }
                                        weight="normal"
                                        onClick={handleSuppliersClick}
                                    >
                                        {isSuppliersEndpoint ? (
                                            <CheckboxFill />
                                        ) : (
                                            <CheckboxBlankLine />
                                        )}
                                        <span className="tracking-0 md:tracking-0 ml-2 text-sm leading-[1.625rem] md:text-base">
                                            Suppliers
                                        </span>
                                    </RoccoButton>
                                ) : undefined}
                            </>
                        }
                        componentBefore={
                            <div ref={filtersRef}>
                                <CollapsibleToggle
                                    filterDrawerOpen={filterDrawerOpen}
                                    toggleFilterDrawer={toggleFilterDrawer}
                                />
                            </div>
                        }
                    />
                )}

                <CollapsiblePageLayout
                    sidePanel={
                        desktop ? (
                            <CategoryFilterNav
                                category="product"
                                searchFilters={searchFiltersSuppliers}
                                hasMarketingView={hasMarketingView}
                                hideCategorySectionTitle={true}
                                hideCategoryBackButton={true}
                            />
                        ) : null
                    }
                    hideSidePanel={!filterDrawerOpen}
                >
                    <div>
                        {desktop &&
                            searchFilters.appliedFilters &&
                            searchFilters.appliedFilters.length > 0 && (
                                <AppliedFilters
                                    filterItems={searchFilters.appliedFilters}
                                    resetFilters={() =>
                                        searchFilters.resetFilters(
                                            resetFilterStates(
                                                searchFilters.filterStates,
                                                [
                                                    ...CATEGORY_IGNORE_FILTER_KEYS,
                                                    'category',
                                                ]
                                            )
                                        )
                                    }
                                />
                            )}

                        {shouldShowSupplierGrid ? (
                            <SupplierGrid filterDrawerOpen={filterDrawerOpen} />
                        ) : shouldShowProductGrid ? (
                            <ProductGrid
                                gridColumns={gridColumns}
                                filterDrawerOpen={filterDrawerOpen}
                                isPurchasableChecked={
                                    searchFilters.filterStates.isPurchasable
                                        ?.checked ?? false
                                }
                            />
                        ) : (
                            <NoResultsSection
                                resultsType={
                                    isSuppliersEndpoint
                                        ? 'suppliers'
                                        : 'products'
                                }
                            />
                        )}
                    </div>
                </CollapsiblePageLayout>

                <div className="my-10 flex flex-col gap-10 md:my-[12.8125rem] md:gap-[13.75rem]">
                    <CategoryPageRecentlyViewed
                        displayMode={
                            appDisplayMode.desktop ? 'desktop' : 'mobile'
                        }
                        items={recentlyViewedProducts}
                    />

                    <CategoryPageFAQ
                        faqSchemas={directoryPageMeta.faqSchemas}
                        footerContent={directoryPageMeta.footerContent}
                    />

                    {desktop && <PageBottomSection openLogin={openLogin} />}
                </div>
            </Container>

            {!uiUpdatesJune2025Enabled && (
                <CategoryPageSearchIsland
                    searchIndex={SearchIndexes.Products}
                    displayMode={appDisplayMode.desktop ? 'desktop' : 'mobile'}
                    placeholders={searchIslandPlaceholders}
                    scrollThreshold={0}
                    forceHide={filterDrawerOpen}
                />
            )}

            <BackToTopButton />
        </Box>
    );
};

export default ProductsCategoryPage;

// ProductGrid component for products
interface ProductGridProps {
    filterDrawerOpen: boolean;
    gridColumns: 1 | 2 | 3 | 4 | 5;
    isPurchasableChecked: boolean;
}

const ProductGrid = React.memo(
    ({
        filterDrawerOpen,
        gridColumns,
        isPurchasableChecked,
    }: ProductGridProps) => {
        const gridRef = useRef<HTMLDivElement | null>(null);
        useCategoryTilesTracking(gridRef);

        const {
            pagination,
            productTilesRocco: productTiles,
            directoryPageMeta,
            ads,
            promotedAds,
            appliedFilterCount,
            amplifiedItems,
        } = useLoaderData<typeof loader>();

        const remixLocation = useLocation();
        const appDisplayMode = useAppDisplayModeContext();
        const theme = useTheme();
        const { toggleFilterDrawer } = useFilterDrawerPreference();
        const { handleFollow, isInDesignBoard } = useHandleFollow();
        const { isSafari } = useDeviceBrowserContext();
        const productTilesRocco = useTilesAdminData(productTiles);

        // Keep the first value returned by loader to avoid changing when loader revalidate
        const adsMemoized = useLocationMemo(ads, ['region']);
        const promotedAdsMemoized = useLocationMemo(promotedAds, ['region']);
        const { desktop } = appDisplayMode;
        const isAnalyticsAmplifierTilesOn = useABTestIsOn(
            'analytics-amplifier-tiles'
        );

        // Process product tiles
        const productBaseTiles = useMemo(() => {
            return productTilesRocco.map((tile) => {
                return (
                    <div
                        key={tile.model.id}
                        className="transform-gpu will-change-transform"
                        style={{
                            transition: 'transform 300ms ease',
                        }}
                    >
                        <CategoryPageTileWithAdminActions tile={tile} />
                    </div>
                );
            });
        }, [productTilesRocco]);

        const { width: windowWidth } = useWindowSize();
        const isLargeScreen = windowWidth && windowWidth > 1920;

        // Process all tiles including ads and promoted content
        const processedTiles = useMemo(() => {
            const result = [...productBaseTiles];

            // Add amplified item tiles with positions based on filter drawer state
            const tilePositions =
                filterDrawerOpen && !isLargeScreen
                    ? [3, 6, 9, 12] // When filter drawer is open and screen is not large
                    : [4, 8, 12, 16, 20]; // When filter drawer is closed or screen is large (>1900px)

            if (isAnalyticsAmplifierTilesOn) {
                if (amplifiedItems?.length) {
                    tilePositions.forEach((position, index) => {
                        const item = amplifiedItems[index];
                        if (position < result.length && item) {
                            const amplifiedTile = (
                                <div className="aspect-[6/8] md:aspect-[6/7]">
                                    <AnalyticsAmplifierTile
                                        key={`amplified_${position}`}
                                        image={item.image}
                                        logo={item.logo}
                                        name={item.title}
                                        subtitle={item.aiServiceSummary}
                                        website={item.websiteLink}
                                        id={parseInt(item.id)}
                                        professionalId={parseInt(
                                            item.professionalId
                                        )}
                                        backgroundColor={item.backgroundColor}
                                    />
                                </div>
                            );
                            result.splice(position, 0, amplifiedTile);
                        }
                    });
                }
            }

            // Insert promoted ad tile if conditions are met
            if (
                desktop &&
                pagination.page === 1 &&
                result.length > 0 &&
                promotedAdsMemoized.length &&
                promotedAdsMemoized[0] &&
                promotedAdsMemoized[0].ad
            ) {
                const offset = filterDrawerOpen ? 7 : 9;
                if (result.length >= offset) {
                    const adTile = promotedAdsMemoized[0];
                    const professionalID = adTile.professionalID ?? 0;
                    const imageProps = {
                        src: adTile.image,
                        width: '100%',
                        height: '100%',
                        sizes: getImagesSizes(310),
                        alt: adTile.title,
                    };
                    result.splice(
                        offset,
                        0,
                        <ArchizeenAdvertTile
                            tag={'ARTICLE'}
                            advertCreative={promotedAdsMemoized[0].ad}
                            key={`promoted_${adTile.ID}`}
                            prefetch="intent"
                            image={imageProps}
                            size={!filterDrawerOpen ? 'large' : 'medium'}
                            variant={'08'}
                            variables={{
                                color: theme.siteVariables.colors.charcoal[
                                    '250'
                                ],
                                dynamicSizingWidth: 310,
                                dynamicSizingGapWidth: 0,
                                tilePadding: pxToRem(10),
                            }}
                            onFavourite={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handleFollow(
                                    { id: professionalID },
                                    'ProfessionalPin'
                                );
                            }}
                            isFavourited={isInDesignBoard(
                                professionalID,
                                'ProfessionalPin'
                            )}
                            title={adTile.title}
                            label={{
                                children: adTile.author,
                                variant: '06',
                            }}
                            date={{
                                children: adTile.date,
                                variant: '06',
                            }}
                            readTime={{
                                children: adTile.readTime,
                                variant: '06',
                            }}
                        />
                    );
                }
            }

            // Add DetailedProductSpliter if needed
            const category =
                directoryPageMeta.category !== 'Products'
                    ? directoryPageMeta.category
                    : undefined;
            if (productTilesRocco && isPurchasableChecked) {
                const lastPurchasableItemIndex = findLastIndex(
                    productTilesRocco,
                    (t) => t.model.canPurchase
                );
                const purchasableCount =
                    productTilesRocco?.filter((t) => t.model.canPurchase)
                        .length ?? 0;
                if (
                    purchasableCount === 0 ||
                    (lastPurchasableItemIndex !== undefined &&
                        lastPurchasableItemIndex !== -1)
                ) {
                    const rowIndex = Math.ceil(
                        (lastPurchasableItemIndex + 1) / gridColumns
                    );
                    const insertionIndex = rowIndex * gridColumns;
                    if (insertionIndex < productTilesRocco.length) {
                        const moreProductsComing = (
                            <DetailedProductSpliter
                                key="MoreProductsComing"
                                category={category}
                                columns={gridColumns}
                            />
                        );
                        result.splice(insertionIndex, 0, moreProductsComing);
                    }
                }
            }

            // Add banner ad if no filters are applied
            if (
                appliedFilterCount === 0 &&
                adsMemoized.length &&
                adsMemoized[0]
            ) {
                result.unshift(
                    <AdvertBannerTile
                        key="AdvertBannerTile"
                        advertCreative={adsMemoized[0]}
                        columns={gridColumns}
                        desktop={desktop}
                    />
                );
            }

            return result;
        }, [
            productBaseTiles,
            desktop,
            pagination.page,
            promotedAdsMemoized,
            filterDrawerOpen,
            isLargeScreen,
            theme,
            handleFollow,
            isInDesignBoard,
            directoryPageMeta.category,
            isPurchasableChecked,
            productTilesRocco,
            gridColumns,
            appliedFilterCount,
            adsMemoized,
            amplifiedItems,
            isAnalyticsAmplifierTilesOn,
        ]);

        if (productTilesRocco.length === 0) {
            return <NoResultsSection resultsType={'products'} />;
        }

        return (
            <>
                <VirtualizedGrid
                    items={processedTiles}
                    renderItem={(item) => item}
                    startMarker={
                        <div className="hidden">
                            <CollapsibleToggle
                                filterDrawerOpen={filterDrawerOpen}
                                toggleFilterDrawer={toggleFilterDrawer}
                            />
                        </div>
                    }
                    endMarker={
                        pagination.totalPages > 1 && (
                            <div className="pt-[6.5625rem] md:pt-20">
                                <TilePagination
                                    currentPath={remixLocation}
                                    current={pagination.page}
                                    total={pagination.totalPages}
                                    urlBuilder={buildPaginationUrl}
                                    visibleCount={
                                        appDisplayMode.desktop ? 4 : 3
                                    }
                                />
                            </div>
                        )
                    }
                    className={cn(
                        'grid transition-all duration-300 ease-in-out',
                        filterDrawerOpen
                            ? 'grid-cols-2 md:grid-cols-4 qhd:grid-cols-5'
                            : 'grid-cols-2 md:grid-cols-5',
                        'gap-x-2 gap-y-12 md:gap-x-6 md:gap-y-20'
                    )}
                    enabled={isSafari}
                    initialVisibleCount={15}
                    batchSize={10}
                    loadMoreThreshold={0.7}
                    reduceThreshold={0.3}
                    minVisibleCount={15}
                />
            </>
        );
    }
);

// SupplierGrid component for suppliers
interface SupplierGridProps {
    filterDrawerOpen: boolean;
}

const SupplierGrid = React.memo(({ filterDrawerOpen }: SupplierGridProps) => {
    const { pagination, professionalTilesRocco: professionalTiles } =
        useLoaderData<typeof loader>();

    const remixLocation = useLocation();
    const appDisplayMode = useAppDisplayModeContext();
    const professionalTilesRocco = useTilesAdminData(professionalTiles);

    // Process supplier tiles
    const supplierBaseTiles = useMemo(() => {
        return professionalTilesRocco.map((tile) => {
            return (
                <div
                    key={tile.model.id}
                    className="transform-gpu will-change-transform"
                    style={{
                        transition: 'transform 300ms ease',
                    }}
                >
                    <CategoryPageTileWithAdminActions tile={tile} />
                </div>
            );
        });
    }, [professionalTilesRocco]);

    if (supplierBaseTiles.length === 0) {
        return <NoResultsSection resultsType={'suppliers'} />;
    }

    return (
        <>
            <Grid
                colClasses={
                    filterDrawerOpen
                        ? 'grid-cols-1 md:grid-cols-3 qhd:grid-cols-4'
                        : 'grid-cols-1 md:grid-cols-4'
                }
                rowsGapClass="gap-x-2 gap-y-12 md:gap-x-6 md:gap-y-20"
                className="transition-all duration-300 ease-in-out"
            >
                {supplierBaseTiles}
            </Grid>

            {pagination.totalPages > 1 && (
                <div className="pt-[6.5625rem] md:pt-20">
                    <TilePagination
                        currentPath={remixLocation}
                        current={pagination.page}
                        total={pagination.totalPages}
                        urlBuilder={buildPaginationUrl}
                        visibleCount={appDisplayMode.desktop ? 4 : 3}
                    />
                </div>
            )}
        </>
    );
});
